import "./index.scss";

import { Box, Grid, Modal, Stack } from "@mui/material";
import { ReactComponent as IconAdd } from "assets/images/icons/add.svg";
import { ReactComponent as IconSortVertical } from "assets/images/icons/moving-vertical.svg";
import { ReactComponent as IconTrash } from "assets/images/icons/trash.svg";
import { ReactComponent as DeleteIcon } from "assets/images/square-buttons/close-small.svg";
import Button from "components/Button";
import Input from "components/Input";
import ScreenEmptySection from "components/ScreenEmptySection";
import _ from "lodash";
import { CardioProgram, CardioProgramExercise } from "models/workoutplan";
import { nanoid } from "nanoid";
import { memo, useCallback, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { ReactSortable } from "react-sortablejs";
import { toast } from "react-toastify";

import CardioProgramExerciseSelector from "../CardioProgramExerciseSelector";

const ProgramsExercisesCreateEdit = (props: {
  open: boolean;
  onClose: any;
  isCreate: boolean;
  program: CardioProgram;
}) => {
  const { open, onClose, isCreate, program } = props;
  const defaultEmptyValue: CardioProgram = {
    exercises: [],
    id: 0,
    is_active: 1,
    is_express: 0,
    name: "",
    type: "CARDIO",
  };

  const {
    getValues,
    formState: { errors },
    control,
    setValue,
    trigger,
  } = useForm<CardioProgram>({
    defaultValues: defaultEmptyValue,
  });
  const [currentProgram, setCurrentProgram] = useState<CardioProgram>(defaultEmptyValue);
  const [exercisesList, setExercisesList] = useState(defaultEmptyValue.exercises);
  const [showCardioProgramExerciseSelectorModal, setShowCardioProgramExerciseSelectorModal] =
    useState(false);

  const [programNameCount, setProgramNameCount] = useState(0);
  const programNameLength = 31;

  useEffect(() => {
    if (open) {
      if (!isCreate) {
        setCurrentProgram(program);
        setExercisesList(program.exercises);
        setValue("id", program.id);
        setValue("nanoId", program.nanoId);
        setValue("name", program.name);
        setValue("is_active", program.is_active);
        setValue("is_express", program.is_express);
        setValue("exercises", program.exercises);
      } else {
        setCurrentProgram(defaultEmptyValue);
        setExercisesList(defaultEmptyValue.exercises);
        setValue("id", defaultEmptyValue.id);
        setValue("nanoId", nanoid());
        setValue("name", defaultEmptyValue.name);
        setValue("is_active", defaultEmptyValue.is_active);
        setValue("is_express", defaultEmptyValue.is_express);
        setValue("exercises", defaultEmptyValue.exercises);
      }
    }
  }, [open]);

  useEffect(() => {
    //Update program array after sorting
    setValue("exercises", exercisesList);
  }, [exercisesList]);

  //#region event handlers
  const handleCloseModal = useCallback(() => {
    onClose();
  }, []);

  const handleConfirm = () => {
    trigger().then((validationResult) => {
      if (!validationResult) {
        return;
      }
      const exercises = getValues("exercises");
      if (exercises.length === 0) {
        toast(`No Cardio Exercises addded`, { type: "info" });
      }
      onClose(getValues());
    });
  };

  const handleExerciseTimeValueUpdate = (itemId, value) => {
    let copy = [...exercisesList];
    let indexOfEntry = copy.findIndex((e) => e.id === itemId);
    let newEntry = { ...copy.find((e) => e.id === itemId), time: value };
    copy.splice(indexOfEntry, 1, newEntry);
    setExercisesList(copy);
  };

  const handleRemoveExercise = (itemId) => {
    let copy = [...exercisesList];
    const indexToDelete = copy.findIndex((e) => e.id === itemId);
    copy.splice(indexToDelete, 1);
    setExercisesList(copy);
  };

  const handleAddEditExercises = (newList: any[]) => {
    if (newList) {
      let updatedList = [...exercisesList];
      //Remove from old list if no longer new list
      let indexesToDelete = [];
      updatedList.forEach((e, index) => {
        if (!newList.find((f) => e.id === f.value)) {
          indexesToDelete.push(index);
        }
      });
      for (let i = indexesToDelete.length - 1; i >= 0; i--) {
        updatedList.splice(indexesToDelete[i], 1);
      }
      //Add the entries that don't exist
      newList.forEach((e) => {
        if (!updatedList.find((f) => f.id === e.value)) {
          let newEntry: CardioProgramExercise = {
            distance: 0,
            id: 0,
            name: "",
            time: 0,
            type: "",
          };
          updatedList.push({ ...newEntry, id: e.value, name: e.label, type: e.type });
        }
      });
      setExercisesList(updatedList);
    }
    setShowCardioProgramExerciseSelectorModal(false);
  };
  //#endregion event handlers

  return (
    <Modal
      closeAfterTransition
      BackdropProps={{
        timeout: 500,
      }}
      open={open}
      onClose={handleCloseModal}
    >
      <>
        <form>
          <Stack className="personal-details-modal-container program-exercise-create-edit">
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              marginBottom="40px"
              className="header"
            >
              <h1>{isCreate ? `Create` : `Edit`} Program</h1>
              <DeleteIcon width="24px" height="24px" onClick={handleCloseModal} />
            </Stack>
            <h2 className="headerSpacing">Cardio Program Name</h2>
            <Box mb={"5px"}>
              <Controller
                name="name"
                control={control}
                rules={{ required: true }}
                render={({ field: { onChange, value, ref } }) => (
                  <Stack spacing={1}>
                    <Input
                      value={value}
                      onChange={(v) => {
                        onChange(v);
                        setProgramNameCount(v.target.value.length);
                      }}
                      maxLength={programNameLength}
                      hasError={!_.isEmpty(errors.name)}
                      placeholder="Enter Name"
                    />
                    {errors.name?.type === "required" && (
                      <h4 className="error">This field is required</h4>
                    )}
                  </Stack>
                )}
              />
            </Box>
            <Stack
              mb={"32px"}
              direction={"row"}
              alignItems={"right"}
              justifyContent={"flex-end"}
              gap={1}
            >
              {programNameCount}/{programNameLength}
            </Stack>
            <h2 className="headerSpacing">Exercises</h2>
            <Box className="exercises-section">
              <Grid container>
                <Grid item xs={9} className="header">
                  Exercises List
                </Grid>
                <Grid item xs={2} className="header">
                  Time (in minutes)
                </Grid>
                <Grid item xs={1} className="header" justifyContent={"flex-end"}>
                  <button
                    type="button"
                    className="edit-icon"
                    onClick={() => {
                      setShowCardioProgramExerciseSelectorModal(true);
                    }}
                  >
                    <IconAdd />
                  </button>
                </Grid>
              </Grid>
              <div className="sortableAreaContainer">
                <ReactSortable list={exercisesList} setList={setExercisesList} animation={200}>
                  {exercisesList.map((item, index) => (
                    <Grid
                      container
                      alignItems={"center"}
                      className="exercise-entry"
                      key={`program_exercise_${index}`}
                    >
                      <Grid item xs={9}>
                        <IconSortVertical className="sort-handle" />
                        {item.name}
                      </Grid>
                      <Grid item xs={2} sx={{ whiteSpace: "nowrap" }}>
                        <input
                          value={item.time}
                          type="number"
                          min="0"
                          onChange={(e) => handleExerciseTimeValueUpdate(item.id, e.target.value)}
                        />
                        &nbsp;MINS
                      </Grid>
                      <Grid item xs={1}>
                        <IconTrash onClick={() => handleRemoveExercise(item.id)} />
                      </Grid>
                    </Grid>
                  ))}
                </ReactSortable>
                {!exercisesList.length && <ScreenEmptySection />}
              </div>
            </Box>
            <Stack direction="row" alignItems="center" justifyContent="flex-end" marginTop="30px">
              <Stack direction="row" alignItems="center" spacing={3}>
                <Button label="Cancel" className="text-button" onClick={handleCloseModal} />
                <Button
                  label="Confirm"
                  type="button"
                  className="confirm-button"
                  onClick={() => handleConfirm()}
                />
              </Stack>
            </Stack>
          </Stack>
        </form>
        <CardioProgramExerciseSelector
          open={showCardioProgramExerciseSelectorModal}
          onClose={handleAddEditExercises}
          selectedExercises={exercisesList.map((e) => ({ ...e, value: e.id }))}
        />
      </>
    </Modal>
  );
};

export default memo(ProgramsExercisesCreateEdit);
